import { createTheme } from "@mui/material/styles";

import { breakpoints } from "./breakpoints.ts";
import { colors } from "./colors.ts";
import { accordion } from "./components/accordion.ts";
import { accordionDetails } from "./components/accordionDetails.ts";
import { accordionSummary } from "./components/accordionSummary.ts";
import { autocomplete } from "./components/autocomplete.ts";
import { button } from "./components/button.ts";
import { chip } from "./components/chip.ts";
import { container } from "./components/container.ts";
import { cssBaseline } from "./components/cssBaseline.ts";
import { dataGrid } from "./components/dataGrid.ts";
import { dateTimePicker } from "./components/dateTimePicker.ts";
import { dialog } from "./components/dialog.ts";
import { formControl } from "./components/formControl.ts";
import { formControlLabel } from "./components/formControlLabel.ts";
import { iconButton } from "./components/iconButton.ts";
import { inputBase } from "./components/inputBase.ts";
import { inputLabel } from "./components/inputLabel.ts";
import { link } from "./components/link.ts";
import { menu } from "./components/menu.ts";
import { outlinedInput } from "./components/outlinedInput.ts";
import { pagination } from "./components/pagination.ts";
import { radio } from "./components/radio.ts";
import { select } from "./components/select.ts";
import { muiSwitch } from "./components/switch.ts";
import { tablePagination } from "./components/tablePagination.ts";
import { textField } from "./components/textField.ts";
import { timeline } from "./components/timeline.ts";
import { timelineConnector } from "./components/timelineConnector.ts";
import { timelineContent } from "./components/timelineContent.ts";
import { timelineDot } from "./components/timelineDot.ts";
import { timelineOppositeContent } from "./components/timelineOppositeContent.ts";
import { toggleButton } from "./components/toggleButton.ts";
import { toggleButtonGroup } from "./components/toggleButtonGroup.ts";
import { tooltip } from "./components/tooltip.ts";
import { typography } from "./components/typography.ts";
import { palette } from "./palette.ts";
import { shadows } from "./shadows.ts";

const baseTheme = createTheme({
  cssVariables: true,
  palette,
  breakpoints,
  shadows,
  shape: { borderRadius: 6 },

  typography: {
    fontFamily: "Mulish, Roboto, sans-serif",
    h1: {
      fontWeight: 300,
      fontSize: "96px",
      lineHeight: "112px",
      letterSpacing: "-1.5px",
    },
    h2: {
      fontWeight: 300,
      fontSize: "60px",
      lineHeight: "72px",
      letterSpacing: "-0.5px",
    },
    h3: {
      fontWeight: 400,
      fontSize: "48px",
      lineHeight: "56px",
    },
    h4: {
      fontWeight: 400,
      fontSize: "34px",
      lineHeight: "42px",
      letterSpacing: "0.25px",
      marginBottom: 0,
    },
    h5: {
      fontWeight: 400,
      fontSize: "24px",
      lineHeight: "32px",
      marginBottom: 0,
    },
    h6: {
      fontWeight: 500,
      fontSize: "20px",
      lineHeight: "32px",
      letterSpacing: "0.15px",
      marginBottom: 0,
    },
    subtitle1: {
      fontWeight: 400,
      fontSize: "16px",
      lineHeight: "28px",
      letterSpacing: "0.15px",
      color: colors.coolGrey[700],
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: "14px",
      lineHeight: "22px",
      letterSpacing: "0.1px",
      color: colors.coolGrey[700],
    },
    body1: {
      fontWeight: 400,
      fontSize: "16px",
      lineHeight: "24px",
      letterSpacing: "0.15px",
    },
    body2: {
      fontWeight: 400,
      fontSize: "14px",
      lineHeight: "20px",
      letterSpacing: "0.17px",
    },
    body3: {
      fontWeight: 400,
      fontSize: "12px",
      lineHeight: "12px",
      letterSpacing: 0,
    },
    body4: {
      fontWeight: 400,
      fontSize: "10px",
      lineHeight: "10px",
      letterSpacing: 0,
    },
    buttonSmall: {
      fontFamily: "Mulish, Roboto, sans-serif",
      fontWeight: 700,
      fontSize: "0.8125rem",
      lineHeight: "22px",
      letterSpacing: "0.46px",
    },
    caption: {
      color: colors.coolGrey[700],
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "166%",
      letterSpacing: "0.4px",
    },
    overline: {
      fontWeight: 400,
      fontSize: "12px",
      lineHeight: "32px",
      letterSpacing: "1px",
      textTransform: "uppercase",
    },
  },

  components: {
    MuiAccordion: accordion,
    MuiAccordionDetails: accordionDetails,
    MuiAccordionSummary: accordionSummary,
    MuiAutocomplete: autocomplete,
    MuiButton: button,
    MuiChip: chip,
    MuiContainer: container,
    MuiCssBaseline: cssBaseline,
    MuiDataGrid: dataGrid,
    MuiDateTimePicker: dateTimePicker,
    MuiDialog: dialog,
    MuiFormControl: formControl,
    MuiFormControlLabel: formControlLabel,
    MuiInputBase: inputBase,
    MuiInputLabel: inputLabel,
    MuiOutlinedInput: outlinedInput,
    MuiPagination: pagination,
    MuiIconButton: iconButton,
    MuiLink: link,
    MuiMenu: menu,
    MuiSelect: select,
    MuiSwitch: muiSwitch,
    MuiRadio: radio,
    MuiTablePagination: tablePagination,
    MuiTimeline: timeline,
    MuiTimelineConnector: timelineConnector,
    MuiTimelineContent: timelineContent,
    MuiTimelineDot: timelineDot,
    MuiTimelineOppositeContent: timelineOppositeContent,
    MuiTextField: textField,
    MuiToggleButton: toggleButton,
    MuiToggleButtonGroup: toggleButtonGroup,
    MuiTypography: typography,
    MuiTooltip: tooltip,
  },
});

export const theme = createTheme(baseTheme, {
  typography: {
    h4: {
      ...baseTheme.typography.h4,
      [baseTheme.breakpoints.down("md")]: {
        fontSize: "1.5rem",
      },
    },
  },
});
